import React, { useState } from "react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const blankQuestion = () => ({ topic: "", ao_config: {} });

export const GuidedAssignmentSetup = ({
  value,
  onChange,
  options,
  error,
  busy,
  onSubmit,
  initial = {},
}) => {
  const [validation, setValidation] = useState("");
  const state = value || {
    step: 0,
    count: 1,
    due_date: initial.due_date || "",
    questions: [{ ...blankQuestion(), topic: initial.topic || "" }],
  };
  const update = (patch) => {
    setValidation("");
    onChange({ ...state, ...patch });
  };
  const question = state.questions[state.step - 1];
  const objectives = [...(options?.objectives || [])].sort((a, b) =>
    a.identifier.localeCompare(b.identifier, "en", { numeric: true }),
  );
  const validQuestion = (q) =>
    q?.topic.trim() &&
    Object.keys(q.ao_config).length > 0 &&
    Object.entries(q.ao_config).every(
      ([id, marks]) =>
        objectives.some((ao) => ao.id === id) &&
        Number.isInteger(marks) &&
        marks >= 1 &&
        marks <= 100,
    );
  const settingsValid =
    Number.isInteger(state.count) &&
    state.count >= 1 &&
    state.count <= 20 &&
    Boolean(state.due_date) &&
    dayjs(state.due_date).isValid() &&
    !dayjs(state.due_date).isBefore(dayjs().startOf("day"));
  const updateQuestion = (patch) =>
    update({
      questions: state.questions.map((q, index) =>
        index === state.step - 1 ? { ...q, ...patch } : q,
      ),
    });
  const next = () => {
    if (state.step === 0) {
      if (!settingsValid) {
        setValidation("Choose 1–20 questions and a due date today or later.");
        return;
      }
      if (
        state.questions
          .slice(state.count)
          .some((q) => q.topic || Object.keys(q.ao_config).length) &&
        !window.confirm("Remove the details entered for the extra questions?")
      )
        return;
      update({
        step: 1,
        questions: Array.from(
          { length: state.count },
          (_, index) => state.questions[index] || blankQuestion(),
        ),
      });
    } else if (!validQuestion(question)) {
      setValidation(
        "Describe this question and select objectives with 1–100 marks each.",
      );
    } else update({ step: state.step + 1 });
  };
  const submit = () => {
    if (!state.questions.every(validQuestion)) {
      setValidation("Check every question’s topic, objectives and marks.");
      return;
    }
    onSubmit({
      kind: "assignment",
      name: initial.name || "",
      due_date: state.due_date,
      questions: state.questions,
    });
  };
  return (
    <Stack spacing={2} sx={{ my: 2 }}>
      {state.step === 0 ? (
        <>
          <Typography>
            How many written questions would you like, and when is it due?
          </Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            {[1, 2, 3, 5].map((count) => (
              <Chip
                key={count}
                label={`${count} ${count === 1 ? "question" : "questions"}`}
                color={state.count === count ? "primary" : "default"}
                onClick={() => update({ count })}
                disabled={busy}
              />
            ))}
          </Stack>
          <TextField
            label="Number of questions"
            type="number"
            value={state.count}
            onChange={(event) => update({ count: Number(event.target.value) })}
            slotProps={{ htmlInput: { min: 1, max: 20 } }}
            disabled={busy}
            size="small"
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due date"
              value={state.due_date ? dayjs(state.due_date) : null}
              onChange={(date) =>
                update({
                  due_date: date?.isValid() ? date.format("YYYY-MM-DD") : "",
                })
              }
              disablePast
              format="DD/MM/YYYY"
              disabled={busy}
              slotProps={{
                textField: { size: "small" },
                openPickerButton: { sx: { border: "none" } },
              }}
            />
          </LocalizationProvider>
        </>
      ) : state.step <= state.count ? (
        <>
          <Typography>What should question {state.step} cover?</Typography>
          <TextField
            label={`Question ${state.step} instructions`}
            placeholder="For example, explore the role of betrayal in Romeo and Juliet."
            multiline
            minRows={3}
            value={question.topic}
            onChange={(event) => updateQuestion({ topic: event.target.value })}
            helperText="Required: describe the topic or instructions for this question, then choose its objectives and marks below."
            slotProps={{
              htmlInput: { maxLength: 6000, "aria-required": true },
            }}
            disabled={busy}
          />
          <Typography variant="body2">
            Choose the objectives and marks for this question. Bud will prepare
            the marking criteria.
          </Typography>
          {state.step > 1 && (
            <Button
              size="small"
              disabled={busy}
              onClick={() =>
                updateQuestion({
                  ao_config: { ...state.questions[state.step - 2].ao_config },
                })
              }
            >
              Use the previous question’s objectives and marks
            </Button>
          )}
          {error ? (
            <Typography color="error">
              Unable to load objectives. Reopen Bud to try again.
            </Typography>
          ) : !options ? (
            <Typography>Loading objectives…</Typography>
          ) : objectives.length === 0 ? (
            <Typography>
              No assessment objectives are available for this class.
            </Typography>
          ) : (
            objectives.map((ao) => {
              const selected = Object.hasOwn(question.ao_config, ao.id);
              return (
                <Box key={ao.id}>
                  <Chip
                    label={ao.identifier}
                    color={selected ? "primary" : "default"}
                    aria-pressed={selected}
                    disabled={busy}
                    onClick={() => {
                      const allocation = { ...question.ao_config };
                      if (selected) delete allocation[ao.id];
                      else allocation[ao.id] = "";
                      updateQuestion({ ao_config: allocation });
                    }}
                  />
                  <Typography variant="body2" sx={{ my: 1 }}>
                    {ao.description}
                  </Typography>
                  {selected && (
                    <TextField
                      label={`${ao.identifier} marks`}
                      fullWidth
                      helperText="Required: 1–100 marks."
                      sx={{ mt: 1 }}
                      type="number"
                      size="small"
                      value={question.ao_config[ao.id]}
                      onChange={(event) =>
                        updateQuestion({
                          ao_config: {
                            ...question.ao_config,
                            [ao.id]:
                              event.target.value === ""
                                ? ""
                                : Number(event.target.value),
                          },
                        })
                      }
                      slotProps={{
                        htmlInput: { min: 1, max: 100, "aria-required": true },
                        inputLabel: { shrink: true },
                      }}
                      disabled={busy}
                    />
                  )}
                </Box>
              );
            })
          )}
        </>
      ) : (
        <>
          <Typography>Ready to review your assignment?</Typography>
          <Typography variant="body2">
            {state.count} written questions · Due{" "}
            {dayjs(state.due_date).format("D MMMM YYYY")}
          </Typography>
          {state.questions.map((q, index) => (
            <Box key={index}>
              <Typography variant="subtitle2">Question {index + 1}</Typography>
              <Typography sx={{ overflowWrap: "anywhere" }}>
                {q.topic}
              </Typography>
              <Typography variant="body2">
                {Object.entries(q.ao_config)
                  .map(
                    ([id, marks]) =>
                      `${objectives.find((ao) => ao.id === id)?.identifier || "Unavailable objective"}: ${marks} marks`,
                  )
                  .join(" · ")}
              </Typography>
              <Button
                size="small"
                onClick={() => update({ step: index + 1 })}
                disabled={busy}
              >
                Edit question {index + 1}
              </Button>
            </Box>
          ))}
        </>
      )}
      {validation && (
        <Typography color="error" role="alert">
          {validation}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        {state.step > 0 && (
          <Button
            disabled={busy}
            onClick={() => update({ step: state.step - 1 })}
          >
            Back
          </Button>
        )}
        {state.step <= state.count ? (
          <Button
            variant="contained"
            disabled={
              busy ||
              (state.step === 0
                ? !settingsValid
                : !options || error || !validQuestion(question))
            }
            onClick={next}
          >
            {state.step === state.count ? "Review questions" : "Continue"}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={
              busy ||
              !options ||
              error ||
              !settingsValid ||
              !state.questions.every(validQuestion)
            }
            onClick={submit}
          >
            Prepare proposal
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
